import { useCallback } from "react";

export const useDownloadHierarchyCSV = () => {
  const headers = [
    { key: "organization_id", label: "Organization ID" },
    { key: "name", label: "Company Name" },
    { key: "domain", label: "Domain" },
    { key: "parent_organization_id", label: "Parent Organization ID" },
    { key: "parent_name", label: "Parent Company Name" },
    { key: "hierarchy_depth", label: "Hierarchy Depth" },
    { key: "relationship_type", label: "Relationship Type" },
    { key: "industry", label: "Industry" },
    { key: "employee_count", label: "Employee Count" },
    { key: "year_founded", label: "Year Founded" },
    { key: "country", label: "Country" },
    { key: "city", label: "City" },
    { key: "state", label: "State" },
    { key: "company_type", label: "Company Type" },
    { key: "linkedin_url", label: "LinkedIn URL" },
  ];

  const extractRow = useCallback((org, parent = null) => {
    const isUltimateParent = org.hierarchy_metadata?.is_ultimate_parent;
    const baseRelationship = org.relationship_data?.relationship_type || "";
    const relationship_type = isUltimateParent ? "ultimate parent" : baseRelationship;

    return {
      organization_id: org.organization_id || "",
      name: org.name || "",
      domain: org.domain || "",
      parent_organization_id: parent?.organization_id || "",
      parent_name: parent?.name || "",
      hierarchy_depth: org.hierarchy_metadata?.hierarchy_depth ?? "",
      relationship_type,
      industry: org.industry || "",
      employee_count: org.employee_count || "",
      year_founded: org.year_founded || "",
      country: org.headquarters?.country || "",
      city: org.headquarters?.city || "",
      state: org.headquarters?.state || "",
      company_type: org.company_type || "",
      linkedin_url: org.linkedin_url || "",
    };
  }, []);

  const flattenHierarchy = useCallback(
    (rootNode) => {
      if (!rootNode) {
        return [];
      }

      const rows = [];

      const traverse = (node, parent) => {
        rows.push(extractRow(node, parent));
        node.children?.forEach((child) => traverse(child, node));
      };

      traverse(rootNode, null);
      return rows;
    },
    [extractRow]
  );

  const downloadCSV = useCallback(
    (dataArray, filename = "hierarchy.csv") => {
      const csvRows = [];

      // Add header labels
      csvRows.push(headers.map((h) => h.label).join(","));

      // Add rows
      dataArray.forEach((row) => {
        const values = headers.map(({ key }) => {
          let value = row[key];
          if (typeof value === "string") {
            value = value.replace(/"/g, '""'); // Escape double quotes
            return `"${value}"`;
          }
          return value ?? "";
        });
        csvRows.push(values.join(","));
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      link.click();
    },
    [headers]
  );

  const downloadHierarchyCSV = useCallback(
    (hierarchyData, filename = "hierarchy.csv") => {
      const rows = flattenHierarchy(hierarchyData);
      downloadCSV(rows, filename);
    },
    [flattenHierarchy, downloadCSV]
  );

  return {
    downloadHierarchyCSV,
  };
};
